// This file is a placeholder for creating a product item component.
// You can add your code here.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SelectBox from '../components/ui/SelectBox';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import DropZone from '../components/ui/DropZone';
import DateTimeInput from '../components/ui/DateTimeInput'; // Import DateTimeInput
import DateInput from '../components/ui/DateInput'; // Import DateInput
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { Image, Tag, DollarSign, Package, Store, CalendarDays } from 'lucide-react'; // Added CalendarDays for preview icons
import {
    fetchCatenttypes,
    fetchCatalogsWithCategories,
    fetchUsages,
    fetchStores,
    fetchQtyunits,
    fetchCurrencies,
    readTaxes,
    API_BASE_PATH,
    API_ROOT_PATH,
} from '../apiHelper';

// Initialize the API client
const client = new FastAPIClient();

/**
 * CreateProductItem Component.
 * Allows users to create a single catalog entry entry with comprehensive details.
 * Features: Dynamic dropdowns, image upload, conditional rendering for quantity,
 * live product preview, and integration with backend API.
 * Designed with Tailwind CSS, adhering to the specified design principles.
 */

const CreateProductItem = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // State for logged-in user and various dropdown options
    const [loggedInUser, setLoggedInUser] = useState(null);
    const [catenttypeOptions, setCatenttypeOptions] = useState([]);
    const [catalogOptions, setCatalogOptions] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [attachmentUsageOptions, setAttachmentUsageOptions] = useState([]);
    const [storeOptions, setStoreOptions] = useState([]);
    const [qtyunitOptions, setQtyunitOptions] = useState([]);
    const [currencyOptions, setCurrencyOptions] = useState([]);

    const [loadingData, setLoadingData] = useState(true);
    const [dataLoadError, setDataLoadError] = useState(false);

    // New state to store the full fetched catalogs with their nested categories
    const [allCatalogsWithCategories, setAllCatalogsWithCategories] = useState([]);

    // State for toggling expiry date visibility
    const [showExpiryDate, setShowExpiryDate] = useState(false);

    // Initial state for the catalog entry form
    const initialProductItem = {
        member_id: 0,
        partnumber: '',
        productname: '',
        catenttype_id: 'Item', // Default
        quantitymeasure: 'C62', // Default
        quantitymultiple: 1.0, // Default
        catalog_id: '',
        category_id: '',
        attachmenturl: '',
        attachmentusage_id: 'ProductPhoto', // Default
        mimetype: '',
        mimetypeencoding: '',
        filename: '',
        filesize: 0,
        initialquantity: '', // Renamed from 'initialquantity' to just 'quantity' in context
        store_id: '',
        currency: 'NGN',
        listprice: '',
        costprice: '',
        expirydate: '', // New field
        receiptdate: '', // New field
        inventoryflags: 0, // Default
        buyable: 1, // Changed to 1 (for sale)
    };

    const [productItem, setProductItem] = useState(initialProductItem);
    const [errors, setErrors] = useState({});
    const [submitStatus, setSubmitStatus] = useState('idle');

    // State to hold the selected catalog object for conditional rendering logic and preview
    const [selectedCatalog, setSelectedCatalog] = useState(null);
    // State to hold the selected category object for preview
    const [selectedCategory, setSelectedCategory] = useState(null);
    // State to hold the selected store object for preview
    const [selectedStore, setSelectedStore] = useState(null);
    // State to hold the selected quantity unit object for preview
    const [selectedQtyUnit, setSelectedQtyUnit] = useState(null);

    const [taxOptions, setTaxOptions] = useState([])
    const [selectedTaxCategoryId, setSelectedTaxCategoryId] = useState('')


    // Effect to load user data and all necessary dropdown options
    useEffect(() => {
        const loadAllData = async () => {
            setLoadingData(true);
            setDataLoadError(false);
            const lcUser = localStorage.getItem("user");
            let parsedUser = null;

            if (lcUser) {
                parsedUser = JSON.parse(lcUser);
                setLoggedInUser(parsedUser);
                setProductItem(prev => ({ ...prev, member_id: parsedUser.users_id || 0 }));
            } else {
                showToast("Please log in to create catalog entries.", "info");
                setLoadingData(false);
                setDataLoadError(true);
                navigate('/login'); // Consider redirecting if not logged in
                return;
            }

            try {
                // Fetch all data concurrently
                const [
                    catenttypes,
                    catalogsWithCategoriesData,
                    attachmentUsages,
                    stores,
                    qtyunits,
                    currencies,
                    taxes
                ] = await Promise.all([
                    fetchCatenttypes(),
                    fetchCatalogsWithCategories(parsedUser),
                    fetchUsages(),
                    fetchStores(parsedUser),
                    fetchQtyunits(),
                    fetchCurrencies(),
                    readTaxes(parsedUser)
                ]);

                // Store the raw nested data
                setAllCatalogsWithCategories(catalogsWithCategoriesData);

                // Map data to { label, value } format
                setCatenttypeOptions(catenttypes.map(item => ({
                    label: item.description,
                    value: item.catenttype_id.trim(),
                })));
                // Populate catalog options from the fetched data
                setCatalogOptions(catalogsWithCategoriesData.map(catalog => ({
                    label: catalog.identifier,
                    value: catalog.catalog_id,
                    isService: catalog.identifier.toLowerCase().includes("service")
                })));
                // Initially, category options are empty until a catalog is selected
                setCategoryOptions([]);
                setAttachmentUsageOptions(attachmentUsages.map(item => ({
                    label: item.attachmentusage_id,
                    value: item.attachmentusage_id.trim()
                })));
                setStoreOptions(stores.map(store => ({
                    label: store.identifier,
                    value: store.store_id,
                    address: store.address
                })));
                const mappedQtyUnits = qtyunits.map(item => ({
                    label: item.name,
                    value: item.qtyunit_id.trim(),
                }));
                setQtyunitOptions(mappedQtyUnits);

                // Set initial selectedQtyUnit if a default quantitymeasure exists
                const initialQtyUnit = mappedQtyUnits.find(opt => opt.value === initialProductItem.quantitymeasure);
                setSelectedQtyUnit(initialQtyUnit);

                setCurrencyOptions(currencies.map(currency => ({
                    label: `${currency.setccurr} - ${currency.setcnote}`,
                    value: currency.setccurr,
                })));

                setTaxOptions(taxes.map(tax => ({
                    label: `${tax.code} - ${tax.identifier} (${(tax.rate)}%)`,
                    value: tax.taxcategory_id,
                    description: tax.description,
                })));

            } catch (err) {
                console.error("Error loading catalog entry creation data:", err);
                showToast(err.response?.data?.detail || "Failed to load necessary data. Please try again.", "error");
                setDataLoadError(true);
            } finally {
                setLoadingData(false);
            }
        };

        loadAllData();
    }, [showToast]);

    // Handle input changes for all form fields
    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setProductItem(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value // Convert boolean to 1 or 0 for 'buyable'
        }));

        // Special handling for catalog_id change to update selectedCatalog state and filter categories
        if (name === 'catalog_id') {
            const selected = allCatalogsWithCategories.find(opt => opt.catalog_id === value);
            setSelectedCatalog(selected);
            const itemIsService = selected?.identifier?.toLowerCase().includes("service");

            // Filter categories based on the selected catalog
            const relevantCategories = selected ? selected.categories : [];
            setCategoryOptions(relevantCategories.map(cat => ({
                label: cat.identifier,
                value: cat.category_id,
                description: cat.field1
            })));

            // Reset category_id when catalog changes to prevent invalid selections
            setProductItem(prev => {
                const newState = { ...prev, category_id: '' };
                if (itemIsService) {
                    newState.costprice = '0.0';
                    newState.initialquantity = '0';
                } else {
                    // Reset to empty string if not a service, allowing user to input
                    newState.costprice = '';
                    newState.initialquantity = '';
                }
                return newState;
            });
        } else if (name === 'quantitymeasure') {
            const selected = qtyunitOptions.find(opt => opt.value === value);
            setSelectedQtyUnit(selected);
        }
        // Update selectedCategory for preview
        if (name === 'category_id') {
            const selected = categoryOptions.find(opt => opt.value === value);
            setSelectedCategory(selected);
        }
        // Update selectedStore for preview
        if (name === 'store_id') {
            const selected = storeOptions.find(opt => opt.value === value);
            setSelectedStore(selected);
        }

        // Clear error for the field as user types
        if (errors[name]) {
            setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
        }
    }, [allCatalogsWithCategories, catalogOptions, categoryOptions, storeOptions, qtyunitOptions, errors]);

    // Handle DropZone upload completion
    const handleImageUploadComplete = useCallback((uploadedFiles) => {
        if (uploadedFiles && uploadedFiles.length > 0) {
            const fileData = uploadedFiles[0];
            // console.log('file data:\n', fileData)
            setProductItem(prev => ({
                ...prev,
                attachmenturl: fileData.url,
                filename: fileData.filename,
                mimetype: fileData.mimetype,
                mimetypeencoding: fileData.encoding,
                filesize: fileData.size,
            }));
            showToast("Product image uploaded successfully!", "success");
        }
    }, [showToast]);

    // Client-side validation
    const validateForm = useCallback(() => {
        let newErrors = {};
        let isValid = true;

        const currentItemIsService = selectedCatalog?.identifier?.toLowerCase().includes("service");

        if (!productItem.productname.trim()) { newErrors.productname = 'Product Name is required.'; isValid = false; }
        if (!productItem.catenttype_id) { newErrors.catenttype_id = 'Catalog Entry Type is required.'; isValid = false; }
        if (!currentItemIsService && !productItem.quantitymeasure) { newErrors.quantitymeasure = 'Quantity Unit is required.'; isValid = false; }
        if (!productItem.catalog_id) { newErrors.catalog_id = 'Catalog is required.'; isValid = false; }
        if (!productItem.category_id) { newErrors.category_id = 'Category is required.'; isValid = false; }
        if (!productItem.store_id) { newErrors.store_id = 'Branch is required.'; isValid = false; }
        if (!productItem.currency) { newErrors.currency = 'Currency is required.'; isValid = false; }
        if (productItem.listprice === '' || isNaN(parseFloat(productItem.listprice))) { newErrors.listprice = 'List Price is required and must be a number.'; isValid = false; }
        if (productItem.costprice === '' || isNaN(parseFloat(productItem.costprice))) { newErrors.costprice = 'Cost Price is required and must be a number.'; isValid = false; }

        // New field validations
        if (!currentItemIsService && (productItem.initialquantity === '' || isNaN(parseFloat(productItem.initialquantity)))) { newErrors.initialquantity = 'Quantity is required and must be a number.'; isValid = false; }
        // Removed validation for currentquantity
        if (!currentItemIsService && !productItem.receiptdate) { newErrors.receiptdate = 'Receipt Date is required.'; isValid = false; }
        if (!currentItemIsService && (showExpiryDate && !productItem.expirydate)) { newErrors.expirydate = 'Expiry Date is required when enabled.'; isValid = false; }
        if (!itemIsService && (!productItem.quantitymultiple || parseFloat(productItem.quantitymultiple) <= 0)) {
            newErrors.quantitymultiple = 'Minimum sellable quantity must be greater than 0.';
            isValid = false;
        }


        setErrors(newErrors);
        return isValid;
    }, [productItem, selectedCatalog, showExpiryDate]);

    // Form submission handler
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('loading');
        setErrors({});

        const itemIsService = selectedCatalog?.identifier?.toLowerCase().includes("service");

        if (!validateForm()) {
            setSubmitStatus('error');
            showToast("Please correct the errors in the form.", "error");
            return;
        }

        // Prepare receiptdate with timezone info
        let formattedReceiptDate = null;
        if (productItem.receiptdate) {
            // Convert local datetime string (YYYY-MM-DDTHH:mm) to Date object, then to ISO string (UTC)
            formattedReceiptDate = new Date(productItem.receiptdate).toISOString();
        }

        const payload = {
            member_id: loggedInUser?.users_id || 0,
            partnumber: productItem.partnumber || null,
            productname: productItem.productname,
            catenttype_id: productItem.catenttype_id,
            quantitymeasure: itemIsService ? null : productItem.quantitymeasure,
            quantitymultiple: productItem.quantitymultiple,
            catalog_id: productItem.catalog_id,
            category_id: productItem.category_id,
            attachmenturl: productItem.attachmenturl || null,
            attachmentusage_id: productItem.attachmentusage_id,
            mimetype: productItem.mimetype || null,
            mimetypeencoding: productItem.mimetypeencoding || null,
            filename: productItem.filename || null,
            filesize: productItem.filesize || null,
            initialquantity: itemIsService ? null : parseFloat(productItem.initialquantity), // Now just 'quantity'
            // Removed currentquantity from payload
            store_id: productItem.store_id,
            currency: productItem.currency,
            listprice: parseFloat(productItem.listprice),
            costprice: parseFloat(productItem.costprice),
            taxcategory_id: selectedTaxCategoryId ? Number(selectedTaxCategoryId) : null,
            // Conditionally include expirydate
            expirydate: !itemIsService && (showExpiryDate && productItem.expirydate) ? productItem.expirydate : null,
            receiptdate: formattedReceiptDate, // Use the formatted date with timezone
            inventoryflags: productItem.inventoryflags,
            buyable: productItem.buyable, // Now 1 or 0
        };

        console.log('Submitting catalog entry payload:', payload);

        try {
            // const response = await client.postData('/product/create-item', payload);
            // console.log('catalog entry created successfully:', response);
            // showToast("catalog entry created successfully!", "success");
            // setProductItem(initialProductItem); // Clear form
            // setSelectedCatalog(null); // Clear selected catalog
            // setSelectedCategory(null); // Clear selected category
            // setSelectedStore(null); // Clear selected store
            // setShowExpiryDate(false); // Reset expiry date toggle
            // setSelectedQtyUnit(itemIsService ? null : qtyunitOptions.find(opt => opt.value === initialProductItem.quantitymeasure));
            // setSubmitStatus('success');
        } catch (err) {
            console.error('Failed to create catalog entry:', err);
            const errorMessage = err.response?.data?.detail || `Failed to create catalog entry: ${err.message || err}`;
            showToast(errorMessage, "error");
            setSubmitStatus('error');
        }
    };

    // Determine if quantity field should be displayed based on the selected catalog's identifier
    const itemIsService = selectedCatalog?.identifier?.toLowerCase().includes("service");

    if (loadingData) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <p className="text-text-secondary text-lg">Loading product data...</p>
            </div>
        );
    }

    if (dataLoadError) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
                <p className="text-gmail-red text-lg mb-4">
                    Failed to load necessary data. Please ensure you are logged in and backend services are running.
                </p>
                <div className="flex gap-4 mt-4">
                    {allCatalogsWithCategories.length === 0 && (
                        <Button onClick={() => navigate('/catalogs/create')} variant="secondary">
                            Create Catalogs
                        </Button>
                    )}
                </div>
            </div>
        );
    }

    // Check if essential dropdowns are empty (after initial load)
    const essentialDataMissing = catalogOptions.length === 0 || storeOptions.length === 0 || currencyOptions.length === 0;

    if (essentialDataMissing) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
                <p className="text-gmail-red text-lg mb-4">
                    Missing essential data (Catalogs, Branches, Currencies). Please ensure these are created first.
                </p>
                <div className="flex gap-4 mt-4">
                    {catalogOptions.length === 0 && (
                        <Button onClick={() => navigate('/catalogs/create')} variant="secondary">
                            Create Catalogs
                        </Button>
                    )}
                    {/* Add buttons for Stores, Currencies if they can be created via UI */}
                </div>
            </div>
        );
    }


    return (
        <div className="container mx-auto px-4 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Form */}
                <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6 max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    <div className="mb-6">
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">Create new catalog entry</h1>
                        <p className="text-gray-600 text-md">Enter details for a new product or service item to add to your inventory.</p>
                    </div>

                    <form onSubmit={handleSubmit} noValidate>
                        {/* Basic Product Information */}
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Product Details</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <Input
                                    label="Product Name"
                                    id="productname"
                                    name="productname"
                                    type="text"
                                    placeholder="e.g., Laptop, Consulting Service"
                                    value={productItem.productname}
                                    onChange={handleInputChange}
                                    required
                                    error={errors.productname}
                                />
                                <Input
                                    label="Part Number (Optional)"
                                    id="partnumber"
                                    name="partnumber"
                                    type="text"
                                    placeholder="e.g., ABC-123, SKU-XYZ"
                                    value={productItem.partnumber}
                                    onChange={handleInputChange}
                                    error={errors.partnumber}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <SelectBox
                                    label="Catalog Entry Type"
                                    id="catenttype_id"
                                    name="catenttype_id"
                                    value={productItem.catenttype_id}
                                    onChange={handleInputChange}
                                    options={catenttypeOptions}
                                    required
                                    error={errors.catenttype_id}
                                />
                                {/* Conditionally render Quantity Unit based on itemIsService */}
                                {!itemIsService && (
                                    <SelectBox
                                        label="Quantity Unit"
                                        id="quantitymeasure"
                                        name="quantitymeasure"
                                        value={productItem.quantitymeasure}
                                        onChange={handleInputChange}
                                        options={qtyunitOptions}
                                        required
                                        error={errors.quantitymeasure}
                                    />
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <SearchableSelectBox
                                    label="Catalog"
                                    id="catalog_id"
                                    name="catalog_id"
                                    value={productItem.catalog_id}
                                    onChange={handleInputChange}
                                    options={catalogOptions}
                                    placeholder="Select a catalog"
                                    required
                                    error={errors.catalog_id}
                                />
                                <SearchableSelectBox
                                    label="Category"
                                    id="category_id"
                                    name="category_id"
                                    value={productItem.category_id}
                                    onChange={handleInputChange}
                                    options={categoryOptions} // Dynamically populated
                                    placeholder={productItem.catalog_id ? "Select a category" : "Select a catalog first"}
                                    required
                                    disabled={!productItem.catalog_id} // Disable until catalog is selected
                                    error={errors.category_id}
                                />
                            </div>
                        </div>

                        {/* Pricing & Inventory Information */}
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Pricing & Inventory</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <SelectBox
                                    label="Currency"
                                    id="currency"
                                    name="currency"
                                    value={productItem.currency}
                                    onChange={handleInputChange}
                                    options={currencyOptions}
                                    required
                                    error={errors.currency}
                                />
                                <Input
                                    label="List Price"
                                    id="listprice"
                                    name="listprice"
                                    type="number"
                                    placeholder="e.g., 99.99"
                                    value={productItem.listprice}
                                    onChange={handleInputChange}
                                    required
                                    error={errors.listprice}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <Input
                                    label="Cost Price"
                                    id="costprice"
                                    name="costprice"
                                    type="number"
                                    placeholder="e.g., 50.00"
                                    value={productItem.costprice}
                                    onChange={handleInputChange}
                                    required
                                    error={errors.costprice}
                                    disabled={itemIsService}
                                />
                                {!itemIsService && (
                                    <div className="space-y-4">
                                        <Input
                                            label="Initial Quantity"
                                            id="initialquantity"
                                            name="initialquantity"
                                            type="number"
                                            placeholder="e.g., 100"
                                            value={productItem.initialquantity}
                                            onChange={handleInputChange}
                                            required
                                            error={errors.initialquantity}
                                        />
                                        <Input
                                            label="Minimum Sellable Quantity"
                                            id="quantitymultiple"
                                            name="quantitymultiple"
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            placeholder="e.g., 0.5 = half kilo, 1 = full kilo"
                                            value={productItem.quantitymultiple}
                                            onChange={handleInputChange}
                                            required
                                            error={errors.quantitymultiple}
                                            helperText="Smallest amount customer can buy (e.g., 0.5kg, 1kg, 100g)"
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                                <SearchableSelectBox
                                    label="Applicable Tax"
                                    id="taxcategory_id"
                                    name="taxcategory_id"
                                    value={selectedTaxCategoryId}
                                    onChange={(e) => {
                                        setSelectedTaxCategoryId(e.target.value);
                                        if (errors.taxcategory_id) {
                                            setErrors(prev => ({ ...prev, taxcategory_id: '' }));
                                        }
                                    }}
                                    options={taxOptions}
                                    placeholder="Select tax rule (optional)"
                                    error={errors.taxcategory_id}
                                />
                                <div></div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {/* Removed Current Inventory Quantity Input */}
                                <SearchableSelectBox
                                    label="Branch"
                                    id="store_id"
                                    name="store_id"
                                    value={productItem.store_id}
                                    onChange={handleInputChange}
                                    options={storeOptions}
                                    placeholder="Select a branch"
                                    required
                                    error={errors.store_id}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                {!itemIsService && (
                                    <DateTimeInput
                                        label="Receipt Date"
                                        id="receiptdate"
                                        name="receiptdate"
                                        value={productItem.receiptdate}
                                        onChange={handleInputChange}
                                        required
                                        error={errors.receiptdate}
                                    // Note: datetime-local inputs handle timezone based on user's browser locale.
                                    // For backend, ensure API expects ISO 8601 string or handles conversion.
                                    />
                                )}
                                <div>
                                    {!itemIsService && (
                                        <div className="flex items-center mb-2">
                                            <input
                                                type="checkbox"
                                                id="toggleExpiryDate"
                                                checked={showExpiryDate}
                                                onChange={(e) => setShowExpiryDate(e.target.checked)}
                                                className="form-checkbox h-4 w-4 text-gmail-blue rounded focus:ring-gmail-blue"
                                            />
                                            <label htmlFor="toggleExpiryDate" className="ml-2 text-sm font-medium text-text-secondary">
                                                Expiry Date?
                                            </label>
                                        </div>
                                    )}
                                    {showExpiryDate && (
                                        <DateInput
                                            label="Expiry Date"
                                            id="expirydate"
                                            name="expirydate"
                                            value={productItem.expirydate}
                                            onChange={handleInputChange}
                                            required={showExpiryDate} // Required only if checkbox is checked
                                            error={errors.expirydate}
                                        // Note: date inputs handle timezone based on user's browser locale.
                                        // For backend, ensure API expects ISO 8601 string or handles conversion.
                                        />
                                    )}
                                </div>
                            </div>
                            {/* Updated: Buyable Checkbox - Label updated for clarity and value is 1/0 */}
                            <div className="flex items-center mt-4">
                                <input
                                    type="checkbox"
                                    id="buyable"
                                    name="buyable"
                                    checked={productItem.buyable === 1} // Check if value is 1
                                    onChange={handleInputChange}
                                    className="form-checkbox h-4 w-4 text-gmail-blue rounded focus:ring-gmail-blue"
                                />
                                <label htmlFor="buyable" className="ml-2 text-sm font-medium text-text-secondary">
                                    Is this item for sale?
                                </label>
                            </div>
                        </div>

                        {/* Product Photo Upload */}
                        <div className="mb-8 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Product Photo</h2>
                            <DropZone
                                label="Drag and drop product photo here or click to upload (JPG, PNG)"
                                accept=".jpg,.png,.jpeg"
                                uploadUrl={`${API_BASE_PATH}/uploads/upload`}
                                onUploadComplete={handleImageUploadComplete}
                            />
                            {productItem.attachmenturl && (
                                <div className="mt-4 flex items-center gap-2 text-sm text-text-secondary">
                                    <Image size={16} />
                                    <span>Uploaded: <a href={`${API_ROOT_PATH}${productItem.attachmenturl}`} target="_blank" rel="noopener noreferrer" className="text-gmail-blue hover:underline">View Photo</a></span>
                                </div>
                            )}
                        </div>

                        {/* General Form Error */}
                        {errors.general && <p className="text-gmail-red text-sm text-center mb-4">{errors.general}</p>}

                        {/* Submit Button */}
                        <div className="flex justify-end pt-4">
                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                disabled={submitStatus === 'loading'}
                                className="flex items-center gap-2"
                            >
                                {submitStatus === 'loading' ? 'Creating Item...' : 'Create Item'}
                            </Button>
                        </div>
                    </form>
                </div>

                {/* Right Column: Live Product Preview */}
                <div className="lg:col-span-1 bg-white rounded-lg shadow-md p-6 max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    <div className="p-4 mt-8 border border-gray-200 rounded-2xl w-full max-w-sm mx-auto flex flex-col items-center justify-between text-center">
                        <div className="w-32 h-32 relative mx-auto -mt-16 mb-4 border-4 border-white rounded-xl shadow-lg flex items-center justify-center bg-gray-100">
                            {productItem.attachmenturl ? (
                                <img
                                    src={`${API_ROOT_PATH}${productItem.attachmenturl}`}
                                    alt="Product Photo Preview"
                                    className="w-full h-full object-cover rounded-xl"
                                />
                            ) : (
                                <Image size={64} className="text-gray-400" />
                            )}
                        </div>

                        <div className="flex flex-col mb-4 items-center gap-1 w-full px-2">
                            <h2 className="font-bold text-gray-700 text-lg">
                                {productItem.productname || "Product Name"}
                            </h2>
                            <p className="text-gray-500 text-center text-sm">
                                {productItem.partnumber || "Part Number (Optional)"}
                            </p>
                        </div>

                        <div className="w-full text-left mt-4 border-t border-gray-200 pt-4">
                            <div className="flex items-center gap-2 mb-2 text-text-primary">
                                <DollarSign size={18} className="text-gmail-green" />
                                <span className="font-semibold">List Price:</span> {productItem.currency || 'USD'} {productItem.listprice || '0.00'}
                            </div>
                            <div className="flex items-center gap-2 mb-2 text-text-primary">
                                <DollarSign size={18} className="text-purple-600" />
                                <span className="font-semibold">Cost Price:</span> {productItem.currency || 'USD'} {productItem.costprice || '0.00'}
                            </div>
                            <div className="flex items-center gap-2 mb-2 text-text-primary">
                                <Tag size={18} className="text-gmail-blue" />
                                <span className="font-semibold">Catalog:</span> {selectedCatalog?.identifier || 'N/A'}
                            </div>
                            <div className="flex items-center gap-2 mb-2 text-text-primary">
                                <Tag size={18} className="text-gmail-blue" />
                                <span className="font-semibold">Category:</span> {selectedCategory?.label || 'N/A'}
                            </div>
                            <div className="flex items-center gap-2 mb-2 text-text-primary">
                                <Package size={18} className="text-gmail-yellow" />
                                <span className="font-semibold">Quantity:</span> {productItem.initialquantity || '0'} {selectedQtyUnit?.label || 'Units'}
                            </div>
                            {/* Removed Current Quantity from Preview */}
                            <div className="flex items-center gap-2 text-text-primary">
                                <Store size={18} className="text-gmail-red" />
                                <span className="font-semibold">Branch:</span> {selectedStore?.label || 'N/A'}
                            </div>
                            <div className="flex items-center gap-2 text-text-primary">
                                <CalendarDays size={18} className="text-gray-600" />
                                <span className="font-semibold">Receipt Date:</span> {productItem.receiptdate ? new Date(productItem.receiptdate).toLocaleDateString() : 'N/A'}
                            </div>
                            {showExpiryDate && (
                                <div className="flex items-center gap-2 text-text-primary">
                                    <CalendarDays size={18} className="text-orange-500" />
                                    <span className="font-semibold">Expiry Date:</span> {productItem.expirydate ? new Date(productItem.expirydate).toLocaleDateString() : 'N/A'}
                                </div>
                            )}
                            <div className="flex items-center gap-2 text-text-primary">
                                <Tag size={18} className={`${productItem.buyable === 1 ? 'text-gmail-green' : 'text-gmail-red'}`} />
                                <span className="font-semibold">Saleable:</span> {productItem.buyable === 1 ? 'Yes' : 'No (Internal Use)'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateProductItem;
